import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Rocket, Wrench, Bug, Lightbulb, ThumbsUp, Loader2, Sparkles, TrendingUp, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";

const typeIcons: Record<string, any> = {
  feature: Rocket,
  improvement: Wrench,
  bugfix: Bug,
};

const typeColors: Record<string, string> = {
  feature: "bg-primary/10 text-primary border-primary/20",
  improvement: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  bugfix: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const categoryLabels: Record<string, string> = {
  menu: "Cardápio",
  orders: "Pedidos",
  delivery: "Delivery",
  reports: "Relatórios",
  integrations: "Integrações",
  ui: "Interface",
  performance: "Performance",
  other: "Outro",
};

const statusLabels: Record<string, { label: string; color: string }> = {
  pending: { label: "Pendente", color: "bg-muted text-muted-foreground" },
  approved: { label: "Aprovada", color: "bg-emerald-500/10 text-emerald-600" },
  development: { label: "Desenvolvimento", color: "bg-blue-500/10 text-blue-600" },
  implemented: { label: "Implementada", color: "bg-primary/10 text-primary" },
  under_review: { label: "Em análise", color: "bg-blue-500/10 text-blue-600" },
  planned: { label: "Planejado", color: "bg-purple-500/10 text-purple-600" },
  in_progress: { label: "Em desenvolvimento", color: "bg-amber-500/10 text-amber-600" },
  completed: { label: "Concluído", color: "bg-emerald-500/10 text-emerald-600" },
  rejected: { label: "Rejeitado", color: "bg-destructive/10 text-destructive" },
};

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const Changelog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [changelogs, setChangelogs] = useState<any[]>([]);
  const [featureRequests, setFeatureRequests] = useState<any[]>([]);
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "other",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    setUserId(user?.id || null);

    // Load public changelogs only
    const { data: changelogData } = await supabase
      .from("changelogs")
      .select("*")
      .eq("is_published", true)
      .eq("visibility", "public")
      .order("release_date", { ascending: false });

    setChangelogs(changelogData || []);

    // Load feature requests
    const { data: requestsData } = await supabase
      .from("feature_requests")
      .select("*")
      .order("votes_count", { ascending: false })
      .order("created_at", { ascending: false });

    setFeatureRequests(requestsData || []);

    // Load user votes
    if (user) {
      const { data: votesData } = await supabase
        .from("feature_request_votes")
        .select("feature_request_id")
        .eq("user_id", user.id);

      if (votesData) {
        setUserVotes(new Set(votesData.map(v => v.feature_request_id)));
      }
    }

    setLoading(false);
  };

  const handleVote = async (requestId: string) => {
    if (!userId) {
      toast({ title: "Faça login para votar", variant: "destructive" });
      return;
    }

    const hasVoted = userVotes.has(requestId);

    if (hasVoted) {
      // Remove vote
      await supabase
        .from("feature_request_votes")
        .delete()
        .eq("feature_request_id", requestId)
        .eq("user_id", userId);

      setUserVotes(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });

      setFeatureRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, votes_count: req.votes_count - 1 }
            : req
        )
      );
    } else {
      // Add vote
      await supabase
        .from("feature_request_votes")
        .insert({ feature_request_id: requestId, user_id: userId });

      setUserVotes(prev => new Set(prev).add(requestId));

      setFeatureRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, votes_count: req.votes_count + 1 }
            : req
        )
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      toast({ title: "Faça login para enviar sugestões", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("feature_requests").insert({
        user_id: userId,
        title: formData.title,
        description: formData.description,
        category: formData.category,
      });

      if (error) throw error;

      toast({ title: "Sugestão enviada!", description: "Obrigado pelo seu feedback." });
      setFormData({ title: "", description: "", category: "other" });
      loadData();
    } catch (err: any) {
      toast({ title: "Erro ao enviar", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const groupedChangelogs = changelogs.reduce((acc, log) => {
    if (!acc[log.version]) acc[log.version] = [];
    acc[log.version].push(log);
    return acc;
  }, {} as Record<string, any[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
              <Sparkles className="h-7 w-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-display">Atualizações & Sugestões</h1>
              <p className="text-muted-foreground mt-1">
                Confira as novidades e envie suas ideias para melhorar a plataforma
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="changelog" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="changelog" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Histórico de Versões
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Sugestões
            </TabsTrigger>
          </TabsList>

          {/* Changelog Tab */}
          <TabsContent value="changelog" className="space-y-6">
            {Object.entries(groupedChangelogs).map(([version, logs]: [string, any[]]) => (
              <motion.div
                key={version}
                {...fadeUp}
                transition={{ delay: 0.1 }}
              >
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl font-display">
                        Versão {version}
                      </CardTitle>
                      <Badge variant="outline">
                        {new Date(logs[0].release_date).toLocaleDateString("pt-BR")}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {logs.map((log: any) => {
                      const Icon = typeIcons[log.type] || Rocket;
                      return (
                        <div
                          key={log.id}
                          className="flex gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className={`w-8 h-8 rounded-lg ${typeColors[log.type]} flex items-center justify-center shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium">{log.title}</h4>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {log.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions" className="space-y-6">
            {/* Submit Form */}
            <motion.div {...fadeUp}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-primary" />
                    Enviar Nova Sugestão
                  </CardTitle>
                  <CardDescription>
                    Compartilhe suas ideias para melhorar a plataforma
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Título</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Descreva sua sugestão em poucas palavras"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Categoria</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(categoryLabels).map(([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Descrição</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Descreva sua sugestão em detalhes"
                        rows={4}
                        required
                      />
                    </div>

                    <Button type="submit" disabled={submitting} className="w-full">
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Lightbulb className="h-4 w-4 mr-2" />
                          Enviar Sugestão
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Feature Requests List */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="space-y-3">
              <h3 className="text-lg font-semibold font-display">Sugestões da Comunidade</h3>
              {featureRequests.map((request) => (
                <Card key={request.id}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Vote Button */}
                      <button
                        onClick={() => handleVote(request.id)}
                        disabled={!userId}
                        className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                          userVotes.has(request.id)
                            ? "bg-primary/10 text-primary"
                            : "bg-muted hover:bg-muted/80 text-muted-foreground"
                        }`}
                      >
                        <ThumbsUp className="h-5 w-5" />
                        <span className="text-sm font-medium">{request.votes_count}</span>
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium">{request.title}</h4>
                          <Badge className={statusLabels[request.status]?.color || "bg-muted text-muted-foreground"}>
                            {statusLabels[request.status]?.label || request.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {request.description}
                        </p>
                        {request.public_comment && (
                          <div className="mt-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                            <p className="text-xs font-medium text-primary mb-1 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              Resposta da equipe
                            </p>
                            <p className="text-sm text-foreground">{request.public_comment}</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {categoryLabels[request.category]}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(request.created_at).toLocaleDateString("pt-BR")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {featureRequests.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    Nenhuma sugestão enviada ainda. Seja o primeiro!
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Changelog;
